import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import TemplateModal from "@/components/emails/TemplateModal";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
dayjs.extend(relativeTime);

interface EmailTemplate {
  id: number;
  name: string;
  subject: string;
  content: string;
  category?: string;
  tags?: string[];
  created_at?: string;
  updated_at?: string;
}

export default function EmailTemplates() {
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const { toast } = useToast();

  const { data: templates, isLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates/"],
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: number) => {
      return apiRequest("DELETE", `/api/email-templates/${templateId}`);
    },
    onSuccess: () => {
      toast({
        title: "Template deleted",
        description: "Email template has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates/"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete template",
        description: error.message || "Something went wrong",
        variant: "destructive",
      });
    },
  });

  const handleCreateNew = () => {
    setSelectedTemplate(null);
    setModalMode('create');
    setShowTemplateModal(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setModalMode('edit');
    setShowTemplateModal(true);
  };

  const handleDelete = (templateId: number, templateName: string) => {
    if (window.confirm(`Are you sure you want to delete "${templateName}"? This action cannot be undone.`)) {
      deleteTemplateMutation.mutate(templateId);
    }
  };

  const handleCloseModal = () => {
    setShowTemplateModal(false);
    setSelectedTemplate(null);
  };

  const formatTimeAgo = (dateString: string) => dayjs(dateString).fromNow();

  const displayTemplates = templates || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading templates...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Email Templates</h2>
          <p className="text-sm text-gray-600">Manage your email templates for recruitment campaigns</p>
        </div>
        <Button onClick={handleCreateNew}>
          <i className="fas fa-plus mr-2"></i>
          New Template
        </Button>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayTemplates.map((template) => (
          <div key={template.id} className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow">
            {/* Card Header */}
            <div className="p-4 pb-3 border-b border-gray-100">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900 truncate">
                    {template.name}
                  </h3>
                  <div className="flex items-center gap-2 mt-2">
                    {template.category && (
                      <Badge variant="secondary" className="text-xs">
                        {template.category}
                      </Badge>
                    )}
                    {template.tags && template.tags.slice(0, 2).map((tag) => (
                      <Badge key={tag} variant="outline" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {template.tags && template.tags.length > 2 && (
                      <span className="text-xs text-gray-500">
                        +{template.tags.length - 2} more
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button
                    onClick={() => handleEdit(template)}
                    className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                    title="Edit template"
                  >
                    <i className="fas fa-edit text-sm"></i>
                  </button>
                  <button
                    onClick={() => template.id && handleDelete(template.id, template.name)}
                    className="h-8 w-8 p-0 inline-flex items-center justify-center rounded hover:bg-red-50 text-gray-500 hover:text-red-600"
                    title="Delete template"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                </div>
              </div>
            </div>
            
            {/* Card Content */}
            <div className="p-4 pt-3">
              {/* Subject Preview */}
              <div className="mb-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Subject:</p>
                <p className="text-sm text-gray-600 line-clamp-1">{template.subject}</p>
              </div>
              
              {/* Content Preview */}
              <div className="mb-4">
                <p className="text-sm text-gray-600 line-clamp-3 leading-relaxed">
                  {template.content}
                </p>
              </div>
              
              {/* Stats */}
              <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                <span>Modified {formatTimeAgo(template.updated_at || template.created_at || '')}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {displayTemplates.length === 0 && (
        <div className="text-center py-12">
          <div className="mb-4">
            <i className="fas fa-file-text text-gray-300 text-6xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-600 mb-6">Create your first email template to get started with recruitment campaigns.</p>
          <Button onClick={handleCreateNew} className="bg-primary hover:bg-primary/90">
            <i className="fas fa-plus mr-2"></i>
            Create Your First Template
          </Button>
        </div>
      )}

      {/* Template Modal */}
      <TemplateModal
        isOpen={showTemplateModal}
        onClose={handleCloseModal}
        template={selectedTemplate}
        mode={modalMode}
      />
    </div>
  );
}